import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileCheck, Loader2, KeyRound, CheckCircle2 } from 'lucide-react';
import { useAuth, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (pw !== confirm) { setError('The two passwords do not match'); return; }
    setBusy(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, 'Could not reset your password'));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white"><FileCheck className="h-6 w-6" /></div>
          <span className="text-xl font-bold text-slate-900">BizDocs Manager</span>
        </div>

        {done ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm" data-testid="reset-success">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></div>
            <h2 className="text-xl font-bold text-slate-900">Password updated</h2>
            <p className="mt-1 text-sm text-slate-500">You can now sign in with your new password.</p>
            <Button className="mt-5 h-11 w-full bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/login', { replace: true })} data-testid="reset-goto-login">
              Go to sign in
            </Button>
          </div>
        ) : !token ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <h2 className="text-xl font-bold text-slate-900">Invalid reset link</h2>
            <p className="mt-1 text-sm text-slate-500">This link is missing its token. Please request a new reset link.</p>
            <Button className="mt-5 h-11 w-full bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/login', { replace: true })}>Back to sign in</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3.5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-slate-900">
              <KeyRound className="h-5 w-5 text-blue-600" /><h2 className="text-xl font-bold">Choose a new password</h2>
            </div>
            <div>
              <Label htmlFor="np">New Password</Label>
              <Input id="np" type="password" required autoComplete="new-password" className="mt-1.5 h-11" value={pw}
                onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="reset-new-password" />
            </div>
            <div>
              <Label htmlFor="cp">Confirm Password</Label>
              <Input id="cp" type="password" required autoComplete="new-password" className="mt-1.5 h-11" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat the password" data-testid="reset-confirm-password" />
            </div>
            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={busy} className="h-11 w-full bg-blue-600 hover:bg-blue-700" data-testid="reset-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
